import { apiFetch } from '@/api/request'
import type { ApiResponse } from '@/types/verification'
import {
  resolvePortalAttribution,
  type PortalVisitAttribution,
  type PortalVisitChannel,
} from '@/utils/portalVisitSource'

const API_ORIGIN = (process.env.VUE_APP_API_ORIGIN || '').replace(/\/$/, '')
const PUBLIC_BASE = `${API_ORIGIN}/api/portal`
const ADMIN_BASE = `${API_ORIGIN}/api/admin/portal`

const VISITOR_ID_KEY = 'jangi.visitorId'
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

const fetchOptions: RequestInit = {
  credentials: 'include',
}

async function parseJson<T>(response: Response): Promise<ApiResponse<T>> {
  const contentType = response.headers.get('content-type') || ''
  if (!contentType.includes('application/json')) {
    throw new Error('API 서버에 연결할 수 없습니다. 백엔드와 프론트 dev 서버를 확인해 주세요.')
  }
  const data = (await response.json()) as ApiResponse<T>
  if (!response.ok && !data.message) {
    throw new Error('요청 처리에 실패했습니다.')
  }
  return data
}

function unwrap<T>(result: ApiResponse<T>, fallback: string): T {
  if (!result.success || !result.data) {
    throw new Error(result.message || fallback)
  }
  return result.data
}

function createVisitorId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const rand = (Math.random() * 16) | 0
    const value = char === 'x' ? rand : (rand & 0x3) | 0x8
    return value.toString(16)
  })
}

export function getPortalVisitorId() {
  try {
    const stored = localStorage.getItem(VISITOR_ID_KEY) || ''
    if (UUID_RE.test(stored)) return stored
    const next = createVisitorId()
    localStorage.setItem(VISITOR_ID_KEY, next)
    return next
  } catch {
    return createVisitorId()
  }
}

export type PortalVisitDay = {
  date: string
  visitors: number
  pageviews: number
}

export type PortalVisitSourceRow = {
  channel: PortalVisitChannel
  label: string
  visitors: number
  pageviews: number
}

export type PortalVisitStats = {
  todayVisitors: number
  todayPageviews: number
  yesterdayVisitors: number
  yesterdayPageviews: number
  totalVisitors: number
  totalPageviews: number
  last7days: PortalVisitDay[]
  todaySources: PortalVisitSourceRow[]
  yesterdaySources: PortalVisitSourceRow[]
  totalSources: PortalVisitSourceRow[]
}

const SKIP_LAYOUTS = new Set(['admin', 'seller', 'mall', 'bare'])

export function shouldRecordPortalVisit(layout?: string) {
  return !layout || !SKIP_LAYOUTS.has(layout)
}

let lastRecordAt = 0

function buildVisitPayload(query?: Record<string, unknown>) {
  const attribution = resolvePortalAttribution(query)
  return {
    visitorId: getPortalVisitorId(),
    ...attribution,
  }
}

export async function recordPortalVisit(query?: Record<string, unknown>) {
  const now = Date.now()
  if (now - lastRecordAt < 4000) return
  lastRecordAt = now

  try {
    await apiFetch(`${PUBLIC_BASE}/visit`, {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildVisitPayload(query)),
    })
  } catch {
    /* 방문 기록 실패는 사용자 화면에 보이지 않는다 */
  }
}

export async function fetchAdminVisitStats() {
  const response = await apiFetch(`${ADMIN_BASE}/visit-stats`, fetchOptions)
  const result = await parseJson<{ stats: PortalVisitStats }>(response)
  return unwrap(result, '방문자 통계를 불러오지 못했습니다.').stats
}

export type { PortalVisitAttribution, PortalVisitChannel }
